import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Globe, Sparkles, MessageCircle, Target } from 'lucide-react';
export function PricingSlide() {
  const [customerValue, setCustomerValue] = useState(2500);
  const [targetCustomers, setTargetCustomers] = useState(8);
  const [selectedPlan, setSelectedPlan] = useState<'grow' | 'ads'>('grow');
  const planData = {
    grow: {
      price: 447,
      annual: 5364,
      setup: 1000,
      name: 'Grow',
      monthlyLeadRamp: [0.0, 0.0, 0.05, 0.1, 0.15, 0.25, 0.4, 0.65, 0.8, 0.9, 1.0, 1.0],
      note: 'Strategy consults compound — months 1-4 you build the foundation. Month 8+ is where the work pays off and leads flow consistently.'
    },
    ads: {
      price: 747,
      annual: 8964,
      setup: 1000,
      name: 'Ads',
      monthlyLeadRamp: [0.3, 0.55, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0],
      note: 'Ads generate leads from week 1. Higher upfront ad spend, but by month 2 revenue covers all costs.'
    }
  };
  const current = planData[selectedPlan];
  const totalCost = current.annual + current.setup;
  const monthlyFullLeads = targetCustomers;
  const yearRevenue = current.monthlyLeadRamp.reduce((sum, ramp) => {
    return sum + monthlyFullLeads * ramp * customerValue;
  }, 0);
  const netProfit = yearRevenue - totalCost;
  let cumulativeRevenue = 0;
  let cumulativeCost = current.setup;
  let breakEvenMonth = 13;
  for (let m = 0; m < 12; m++) {
    cumulativeRevenue += monthlyFullLeads * current.monthlyLeadRamp[m] * customerValue;
    cumulativeCost += current.price;
    if (cumulativeRevenue >= cumulativeCost && breakEvenMonth === 13) {
      breakEvenMonth = m + 1;
    }
  }
  const formatCurrency = (val: number) => {
    if (Math.abs(val) >= 1000000) return (val < 0 ? '-' : '') + '$' + (Math.abs(val) / 1000000).toFixed(1) + 'M';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const pricingTiers = [{
    name: 'Pro',
    price: 97,
    desc: 'Software only — the tools, none of the strategy',
    features: ['LetLois CRM access', 'Business texting', 'Website included'],
    excluded: ['Strategy consults', 'AI-SEO content strategy', 'Keyword targeting', 'Paid ads management', 'Campaign optimization'],
    cta: 'Start with Pro',
    href: 'https://link.letlois.com/payment-link/6a2af46771a0aa761e464443',
    highlight: false,
    focusIcon: Sparkles,
    focusLabel: 'Platform',
    focusColor: 'text-primary',
    focusBg: 'bg-primary/10',
    footnote: null
  }, {
    name: 'Grow',
    price: 447,
    desc: 'Strategy + organic growth for local businesses',
    features: ['Everything in Pro', '2 hours/month strategy consult', 'AI-SEO content strategy', 'Advanced keyword targeting', 'Organic traffic growth'],
    excluded: ['Paid ads management'],
    cta: 'Start with Grow',
    href: 'https://link.letlois.com/payment-link/6a56bca2b1a0a3e48f24d0d7',
    highlight: true,
    badge: 'MOST POPULAR',
    focusIcon: MessageCircle,
    focusLabel: 'Strategy',
    focusColor: 'text-green-400',
    focusBg: 'bg-green-500/10',
    footnote: null
  }, {
    name: 'Ads',
    price: 747,
    desc: 'Strategy + paid ads for fast lead generation',
    features: ['Everything in Grow', '1 campaign (Google or Facebook ads)', 'Campaign creation & testing', 'Landing page optimization', 'Dual traffic strategy'],
    excluded: [],
    cta: 'Choose Ads',
    href: 'https://link.letlois.com/payment-link/6a56bd1eb1a0a3e48f24d0d9',
    highlight: false,
    focusIcon: Target,
    focusLabel: 'Paid Growth',
    focusColor: 'text-red-400',
    focusBg: 'bg-red-500/10',
    footnote: '+$650/mo per additional campaign'
  }];
  return <section id="pricing" className="min-h-screen bg-dark py-24 px-4 flex flex-col justify-center" data-id="element-369" data-mp="26oijg7arvz9o">
      <div className="max-w-7xl mx-auto w-full" data-id="element-370" data-mp="2bpptkd0e1aji">
        <div className="text-center mb-16" data-id="element-371" data-mp="2gnholfwbxcrx">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-372" data-mp="2gm6jncr5dyz8">
            Three Plans. Pick Your Path.
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-373" data-mp="1xpoqqs0kbt1e">
            Choose the growth strategy that fits your business.
          </motion.p>
        </div>

        {/* Pricing Cards - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20 max-w-5xl mx-auto" data-id="element-374" data-mp="39h4llruyfdff">
          {pricingTiers.map((tier, index) => <motion.div key={tier.name} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`relative flex flex-col p-6 rounded-2xl bg-dark-card border transition-all duration-300 ${tier.highlight ? 'border-primary shadow-[0_0_30px_rgba(26,139,246,0.15)] md:scale-105 z-10' : 'border-white/10 hover:border-white/20'}`} data-id="element-375" data-mp="34il0tus3k7on" data-mpi={index}>
              {tier.highlight && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-376" data-mp="2258gy86qtcet">
                  {tier.badge}
                </div>}

              {/* Focus icon + label - top right */}
              <div className="absolute top-5 right-5" data-id="element-377" data-mp="1x7ytgy2wzatm">
                <div className={`flex items-center gap-1.5 ${tier.focusBg} px-2.5 py-1.5 rounded-lg`} data-id="element-378" data-mp="2kxax7ifh2qoj">
                  <tier.focusIcon size={14} className={tier.focusColor} data-id="element-379" data-mp="2put2myua6nrs" />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${tier.focusColor}`} data-id="element-380" data-mp="2c5iujtwowlui">
                    {tier.focusLabel}
                  </span>
                </div>
              </div>

              <div className="mb-6 pr-4" data-id="element-381" data-mp="33tsndveoaiio">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-382" data-mp="38n5ecc6tr1el">
                  {tier.name}
                </h3>
                <p className="text-sm text-text-muted mb-4 leading-relaxed pr-16" data-id="element-383" data-mp="x6lcmty13zbp">
                  {tier.desc}
                </p>
                <div className="flex items-baseline" data-id="element-384" data-mp="s6h4br79yawk">
                  <span className="text-4xl font-bold text-primary" data-id="element-385" data-mp="16xppno459a3w">
                    ${tier.price}
                  </span>
                  <span className="text-text-dim ml-2" data-id="element-386" data-mp="11yv78na39nkb">/mo</span>
                </div>
                {tier.footnote && <p className="text-[11px] text-text-dim mt-2 italic" data-id="element-387" data-mp="ealro33gxw1m">
                    {tier.footnote}
                  </p>}
              </div>

              <div className="flex-1 space-y-3 mb-8" data-id="element-388" data-mp="o7q77k2t6t9g">
                {tier.features.map((feature, i) => <div key={i} className="flex items-start text-sm text-white/90" data-id="element-389" data-mp="j6alrqwpykqj" data-mpi={i}>
                    <Check size={16} className="text-green-500 mr-2 mt-0.5 shrink-0" data-id="element-390" data-mp="1unakb49ntvt0" />
                    <span data-id="element-391" data-mp="3o7cac51flyq5">{feature}</span>
                  </div>)}
                {tier.excluded.map((feature, i) => <div key={i} className="flex items-start text-sm text-text-dim/50" data-id="element-392" data-mp="1yjeppb8cv22" data-mpi={i}>
                    <X size={16} className="text-red-500/50 mr-2 mt-0.5 shrink-0" data-id="element-393" data-mp="6q1ewb5firlz" />
                    <span data-id="element-394" data-mp="fpxwjsteiq7j">{feature}</span>
                  </div>)}
              </div>

              <a href={tier.href} target="_blank" rel="noopener noreferrer" className={`w-full py-3 rounded-full font-bold transition-all text-center block ${tier.highlight ? 'bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/25' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-id="element-395" data-mp="kp11ld9dswfi">
                {tier.cta}
              </a>
            </motion.div>)}
        </div>

        {/* ROI Calculator */}
        <div className="bg-gradient-to-br from-dark-card to-primary/5 rounded-2xl p-8 border border-white/10 mb-16" data-id="element-396" data-mp="2f595vkhfw35l">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-397" data-mp="2a5ms99xp8kw9">
            Calculate Your ROI
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-398" data-mp="257uva1i768l7">
            Conservative projections based on Ahrefs, WordStream & CallRail
            industry data
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-399" data-mp="2xv7go8cmgacx">
            <div className="space-y-6" data-id="element-400" data-mp="2xtwegcytranx">
              <div data-id="element-401" data-mp="2svuqpeqgen64">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-402" data-mp="2sv2x29gf2zih">
                  Average Customer Value
                </label>
                <div className="relative" data-id="element-403" data-mp="1hhh6cjuwspuu">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-dim" data-id="element-404" data-mp="1hia6h1zd4h40">
                    $
                  </span>
                  <input type="number" value={customerValue} onChange={e => setCustomerValue(Number(e.target.value))} className="w-full bg-black/30 border border-white/10 rounded-xl py-3 pl-8 pr-4 text-white focus:border-primary focus:outline-none transition-colors" data-id="element-405" data-mp="1ckqte5eeiadn" />
                </div>
              </div>

              <div data-id="element-406" data-mp="9paqg9v8at2o">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-407" data-mp="9orj1n37fn2w">
                  Target New Customers / Month
                </label>
                <input type="number" value={targetCustomers} onChange={e => setTargetCustomers(Number(e.target.value))} className="w-full bg-black/30 border border-white/10 rounded-xl py-3 px-4 text-white focus:border-primary focus:outline-none transition-colors" data-id="element-408" data-mp="5ps8nmypey42" />
              </div>

              <div data-id="element-409" data-mp="2gh228dn2a4v6">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-410" data-mp="2ghv4b1b2s66z">
                  Select Plan
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-411" data-mp="10wjbdg64i2y7">
                  {(['grow', 'ads'] as const).map(plan => <button key={plan} onClick={() => setSelectedPlan(plan)} className={`py-2 rounded-lg text-sm font-medium transition-all ${selectedPlan === plan ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-white/5 text-text-muted hover:bg-white/10'}`} data-id="element-412" data-mp="m6vcsmpbtie6">
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </button>)}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4" data-id="element-413" data-mp="88ds6xupwm0h">
              <div className="bg-black/20 rounded-xl p-6 border border-white/5" data-id="element-414" data-mp="1xmtpr1nmkoko">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-415" data-mp="22lwk9s5zbcee">
                  <div data-id="element-416" data-mp="22jsjhj3dmtic">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-417" data-mp="214pw0skfay6i">
                      Year 1 Revenue
                    </p>
                    <p className="text-2xl font-bold text-white" data-id="element-418" data-mp="1w4bm14z9he5u">
                      {formatCurrency(yearRevenue)}
                    </p>
                  </div>
                  <div data-id="element-419" data-mp="1n5zxd8hygqg1">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-420" data-mp="1n6jckhg810qd">
                      Total Investment
                    </p>
                    <p className="text-2xl font-bold text-white" data-id="element-421" data-mp="2ewnw6dw5pjqj">
                      {formatCurrency(totalCost)}
                    </p>
                  </div>
                  <div data-id="element-422" data-mp="2a3uszugc6jgk">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-423" data-mp="2642fd3c35n4c">
                      Net Profit
                    </p>
                    <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-424" data-mp="32n0aq7lwqeq3">
                      {formatCurrency(netProfit)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Break-even highlight */}
              <motion.div key={selectedPlan + customerValue + targetCustomers} initial={{
              scale: 0.98,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className={`rounded-xl p-5 border ${breakEvenMonth <= 12 ? 'bg-green-500/10 border-green-500/20' : 'bg-yellow-500/10 border-yellow-500/20'}`} data-id="element-425" data-mp="pdt8kfi22js9">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-426" data-mp="1d1t7w3tfniig">
                  <div data-id="element-427" data-mp="1h2d5f8hx4o54">
                    <p className={`font-bold text-lg ${breakEvenMonth <= 12 ? 'text-green-400' : 'text-yellow-400'}`} data-id="element-428" data-mp="1h4z9uz9iinj4">
                      {breakEvenMonth <= 12 ? `Break-even: Month ${breakEvenMonth}` : 'Break-even: 12+ months'}
                      {breakEvenMonth <= 12 && selectedPlan === 'grow' && ' — then profit skyrockets'}
                    </p>
                    <p className="text-text-muted text-sm mt-1" data-id="element-429" data-mp="1n67ymv804r52">
                      {current.note}
                    </p>
                  </div>

                  {/* Mini month timeline */}
                  <div className="flex items-end gap-1" data-id="element-430" data-mp="uhkamrvvbnnt">
                    {current.monthlyLeadRamp.map((ramp, i) => <div key={i} className="flex flex-col items-center" data-id="element-431" data-mp="14dnnarocj4p0" data-mpi={i}>
                        <div className={`w-3 rounded-t-sm ${i < breakEvenMonth ? 'bg-red-500/40' : 'bg-green-500/60'}`} style={{
                      height: `${Math.max(ramp * 32, 2)}px`
                    }} data-id="element-432" data-mp="14bkobi57u185" />
                        {(i === 0 || i === 5 || i === 11) && <span className="text-[7px] text-text-dim mt-0.5" data-id="element-433" data-mp="2ich1zixgp1n2">
                            {i + 1}
                          </span>}
                      </div>)}
                    <span className="text-[7px] text-text-dim ml-0.5 self-end mb-0.5" data-id="element-434" data-mp="29ede4fz07ss3">
                      mo
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Plan-specific insight */}
              <div className="bg-blue-900/10 border border-blue-500/15 rounded-lg px-4 py-3" data-id="element-435" data-mp="363cruaicxmro">
                <p className="text-xs text-blue-300" data-id="element-436" data-mp="2sby7o0cprfj1">
                  <span className="font-bold" data-id="element-437" data-mp="2s9bzbyi00vzr">
                    {current.name} Plan Insight:
                  </span>{' '}
                  {selectedPlan === 'grow' && "Strategy + SEO is a compounding asset. Months 1-4 you're building domain authority and refining strategy — expect minimal leads. Months 5-7 rankings start climbing. Month 8+ is where it pays off big. Average local SEO ROI after 12 months: 200-300% (Ahrefs)."}
                  {selectedPlan === 'ads' && 'Google or Facebook Ads for local services average $30-80 CPA with 3-5x ROAS (WordStream). Ads produce leads from week 1 while SEO builds behind the scenes. Month 2 typically covers all costs including ad spend.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Caveat & Trust */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-438" data-mp="3az7352fsyq9z">
          <div className="bg-blue-900/10 border border-blue-500/20 rounded-xl p-4 mb-8" data-id="element-439" data-mp="3axvwmuuz34f6">
            <p className="text-sm text-blue-200" data-id="element-440" data-mp="36w0y1y4ff553">
              <span className="font-bold" data-id="element-441" data-mp="31ztqk0wdtll1">Honest Note:</span> We're built on
              GoHighLevel—the most powerful (and least supported) platform in
              the world. That's why we exist. We're the support team you need.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-text-dim" data-id="element-442" data-mp="2hwxu3yhi4dlz">
            <span className="flex items-center" data-id="element-443" data-mp="2hz0kwkqhaarr">
              <Check size={16} className="text-primary mr-2" data-id="element-444" data-mp="2muzc0g36y6vv" /> Join 200+
              businesses
            </span>
            <span className="flex items-center" data-id="element-445" data-mp="2rwewo71gibdq">
              <Check size={16} className="text-primary mr-2" data-id="element-446" data-mp="2wu6rp9xeedm5" /> Setup support
              included
            </span>
            <span className="flex items-center" data-id="element-447" data-mp="1z6y6zh5818l7">
              <Check size={16} className="text-primary mr-2" data-id="element-448" data-mp="1z8997cj0q8a7" /> Money-back
              guarantee
            </span>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center" data-id="element-449" data-mp="293u7p0ovoqf7">
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => {
          document.getElementById('pricing')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="bg-primary hover:bg-primary-hover text-white text-xl font-bold py-5 px-10 rounded-full shadow-xl shadow-primary/30 mb-4 flex items-center mx-auto" data-id="element-450" data-mp="292rhyw3aku0v">
            <Globe className="mr-3" data-id="element-451" data-mp="2vm96fczmtl6s" />
            Schedule an Onboarding Call
          </motion.button>
          <p className="text-text-muted" data-id="element-452" data-mp="34kktj51ai0uh">Pick a plan above to get started.</p>
        </div>
      </div>
    </section>;
}